import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProductPicker } from "@/components/ProductPicker";
import { VendorPicker } from "@/components/VendorPicker";
import { SimpleSelect } from "@/components/Filters";
import { useProducts } from "@/hooks/useData";
import { api, errMsg, isConflict, notifyStockChanged } from "@/lib/api";
import { ConflictDialog } from "@/components/ConflictDialog";
import { todayStr, fmtINR } from "@/lib/format";

let rowSeq = 0;
const rowKey = () => `row-${++rowSeq}`;

const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

const ItemsEditor = ({ items, setItems, showPrice }) => {
  const { products } = useProducts();
  const upd = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const pick = (i, id) => {
    const p = products.find((x) => x.id === id);
    setItems(items.map((it, j) => (j === i ? { ...it, product_id: id, price: it.price || p?.price || 0 } : it)));
  };
  return (
    <div className="space-y-2" data-testid="order-items-editor">
      {items.map((it, i) => (
        <div key={it._k || `${i}-${it.product_id}`} className="flex items-center gap-2">
          <div className="flex-1 min-w-0"><ProductPicker products={products} value={it.product_id} onChange={(id) => pick(i, id)} testid={`item-product-${i}`} wrap /></div>
          <Input type="number" min="1" placeholder="Qty" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-20 font-mono" data-testid={`item-qty-${i}`} />
          {showPrice && <Input type="number" step="1" placeholder="Price" value={it.price} onChange={(e) => upd(i, "price", e.target.value)} className="w-24 font-mono" data-testid={`item-price-${i}`} />}
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-1 text-muted-foreground hover:text-red-400" data-testid={`item-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { _k: rowKey(), product_id: "", qty: "", price: "" }])} data-testid="item-add-btn">
        <Plus className="h-4 w-4" /> Add item
      </Button>
    </div>
  );
};

const cleanItems = (items) => items.filter((i) => i.product_id && Number(i.qty) > 0)
  .map((i) => ({ product_id: i.product_id, qty: Number(i.qty), price: Number(i.price) || 0 }));

const WH_OPTS = [["A", "Warehouse A"], ["B", "Warehouse B"]];

const soForm = (o) => (o ? { customer: o.customer, phone: o.phone, order_date: o.order_date, warehouse: o.warehouse, notes: o.notes }
  : { customer: "", phone: "", order_date: todayStr(), warehouse: "A", notes: "" });

const poForm = (o, prefill) => (o ? { kind: o.kind, supplier: o.supplier, supplier_address: o.supplier_address || "", supplier_gstin: o.supplier_gstin || "", order_date: o.order_date, expected_date: o.expected_date || "", warehouse: o.warehouse, notes: o.notes }
  : { kind: prefill?.kind || "manufacturing", supplier: "", supplier_address: "", supplier_gstin: "", order_date: todayStr(), expected_date: "", warehouse: "A", notes: "" });

const useOrderEdit = (path, order, apply) => {
  const [rev, setRev] = useState(0);
  const [conflict, setConflict] = useState(false);
  const reload = async () => {
    setConflict(false);
    try {
      const { data } = await api.get(`${path}/${order.id}`);
      if (data.status !== "pending") toast.warning(`This order is now ${data.status} and can no longer be edited`);
      apply(data);
      setRev(data.rev || 0);
      notifyStockChanged();
      toast.success("Latest version loaded");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fail = (err) => (isConflict(err) ? setConflict(true) : toast.error(errMsg(err)));
  const dialog = <ConflictDialog open={conflict} what="order" onClose={() => setConflict(false)} onReload={reload} />;
  return { rev, setRev, fail, dialog };
};

export const SalesOrderDialog = ({ open, onOpenChange, order }) => {
  const [f, setF] = useState({});
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const ed = useOrderEdit("/sales-orders", order, (o) => { setF(soForm(o)); setItems(o.items.map((i) => ({ ...i }))); });
  const { setRev } = ed;
  useEffect(() => {
    if (!open) return;
    setF(soForm(order));
    setItems(order ? order.items.map((i) => ({ ...i })) : [{ product_id: "", qty: "", price: "" }]);
    setRev(order?.rev || 0);
  }, [open, order, setRev]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const total = cleanItems(items).reduce((s, i) => s + i.qty * i.price, 0);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, items: cleanItems(items), expected_rev: ed.rev };
    if (!body.items.length) return toast.error("Add at least one item");
    if (busy) return;
    setBusy(true);
    try {
      if (order) await api.put(`/sales-orders/${order.id}`, body); else await api.post("/sales-orders", body);
      toast.success(order ? "Order updated" : "Sales order created");
      notifyStockChanged();
      onOpenChange(false);
    } catch (err) { ed.fail(err); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-order-dialog">
        <DialogHeader><DialogTitle>{order ? `Edit ${order.order_no}` : "New sales order"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Customer"><Input required value={f.customer || ""} onChange={set("customer")} data-testid="so-customer-input" /></F>
            <F label="Phone"><Input value={f.phone || ""} onChange={set("phone")} data-testid="so-phone-input" /></F>
            <F label="Order date"><Input type="date" required value={f.order_date || ""} onChange={set("order_date")} data-testid="so-date-input" /></F>
            <F label="Dispatch from"><div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm" data-testid="so-warehouse-fixed">Warehouse A (sales warehouse)</div></F>
          </div>
          <F label="Items"><ItemsEditor items={items} setItems={setItems} showPrice /></F>
          <F label="Notes"><Textarea value={f.notes || ""} onChange={set("notes")} rows={2} data-testid="so-notes-input" /></F>
          <DialogFooter className="items-center sm:justify-between">
            <span className="font-mono text-sm" data-testid="so-total">Total: {fmtINR(total)}</span>
            <Button type="submit" disabled={busy} data-testid="so-save-btn">{busy ? "Saving…" : "Save order"}</Button>
          </DialogFooter>
        </form>
        {ed.dialog}
      </DialogContent>
    </Dialog>
  );
};

export const PurchaseOrderDialog = ({ open, onOpenChange, order, prefill }) => {
  const [f, setF] = useState({});
  const [items, setItems] = useState([]);
  const ed = useOrderEdit("/purchase-orders", order, (o) => { setF(poForm(o)); setItems(o.items.map((i) => ({ ...i }))); });
  const { setRev } = ed;
  useEffect(() => {
    if (!open) return;
    setF(poForm(order, prefill));
    setItems(order ? order.items.map((i) => ({ ...i })) : prefill?.items?.length ? prefill.items : [{ product_id: "", qty: "", price: "" }]);
    setRev(order?.rev || 0);
  }, [open, order, prefill, setRev]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const [busy, setBusy] = useState(false);
  const pickSupplier = (v) => setF((p) => ({ ...p, supplier: v.name, supplier_address: v.address || p.supplier_address, supplier_gstin: v.gstin || p.supplier_gstin }));
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, expected_date: f.expected_date || null, items: cleanItems(items), expected_rev: ed.rev };
    if (!body.items.length) return toast.error("Add at least one item");
    if (busy) return;
    setBusy(true);
    try {
      if (order) await api.put(`/purchase-orders/${order.id}`, body); else await api.post("/purchase-orders", body);
      toast.success(order ? "Order updated" : "Order created");
      notifyStockChanged();
      onOpenChange(false);
    } catch (err) { ed.fail(err); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-order-dialog">
        <DialogHeader><DialogTitle>{order ? `Edit ${order.order_no}` : "New purchase / manufacturing order"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Order type"><SimpleSelect value={f.kind || "manufacturing"} onChange={set("kind")} testid="po-kind-select" className="h-9 w-full"
              options={[["manufacturing", "Manufacturing order"], ["purchase", "Purchase order"]]} /></F>
            <F label={f.kind === "purchase" ? "Supplier" : "Workshop / vendor"}><VendorPicker value={f.supplier || ""} onPick={pickSupplier} testid="po-supplier-input" /></F>
            <F label="Supplier address"><Input value={f.supplier_address || ""} onChange={set("supplier_address")} data-testid="po-supplier-address-input" /></F>
            <F label="Supplier GSTIN"><Input value={f.supplier_gstin || ""} onChange={set("supplier_gstin")} data-testid="po-supplier-gstin-input" /></F>
            <F label="Order date"><Input type="date" required value={f.order_date || ""} onChange={set("order_date")} data-testid="po-date-input" /></F>
            <F label="Expected date"><Input type="date" value={f.expected_date || ""} onChange={set("expected_date")} data-testid="po-expected-input" /></F>
            <F label="Receive into"><SimpleSelect value={f.warehouse || "A"} onChange={set("warehouse")} options={WH_OPTS} testid="po-warehouse-select" className="h-9 w-full" /></F>
          </div>
          <F label="Items"><ItemsEditor items={items} setItems={setItems} showPrice /></F>
          <F label="Notes"><Textarea value={f.notes || ""} onChange={set("notes")} rows={2} data-testid="po-notes-input" /></F>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="po-save-btn">{busy ? "Saving…" : "Save order"}</Button></DialogFooter>
        </form>
        {ed.dialog}
      </DialogContent>
    </Dialog>
  );
};

export const DispatchDialog = ({ open, onOpenChange, order }) => {
  const [date, setDate] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/sales-orders/${order.id}/dispatch`, { date });
      toast.success(`${order.order_no} dispatched — stock OUT recorded`);
      notifyStockChanged();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dispatch-dialog">
        <DialogHeader><DialogTitle>Dispatch {order?.order_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Stock will be deducted (OUT) from Warehouse {order?.warehouse} on this date.</p>
        <F label="Dispatch date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dispatch-date-input" /></F>
        <DialogFooter><Button onClick={go} disabled={busy} data-testid="dispatch-confirm-btn">{busy ? "Saving…" : "Confirm dispatch"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

